import type { ChatType } from '@kanbu/schema';
import {
  TonePreset,
  ResponseLengthPreset,
  AgentType,
  Gender,
} from '@kanbu/schema/enums';
import { AgentDefaults } from '@kanbu/shared';
import { useLingui } from '@lingui/react/macro';
import { useFormContext, type TypedFormState } from '@utima/ui-informed';
import type { RelevantParams } from 'informed';
import { MessageCircle, PhoneCall } from 'lucide-react';

import { Card } from '@/components/card/Card';
import type { FormSchemaFieldsDef } from '@/components/form/formUtils';
import { SchemaFields } from '@/components/form/SchemaFields';
import { useFieldsSchema } from '@/components/form/useFieldsSchema';
import { useEnumTranslations } from '@/hooks';
import { useAccess } from '@/hooks/useAccess';
import { useCurrentOrganization } from '@/hooks/useCurrentOrganization';

import { AgentSelector } from '../agent-selector/agent-selector';

/**
 * Shared `$relevant` gate for fields the voice runtime ignores or
 * force-overrides — see `agentConfig.responseLengthPreset` and the
 * showSources/showFollowUpQuestions/imageSupport group below.
 */
function isRelevantForChatOnly({ formState }: RelevantParams) {
  return (
    (formState as TypedFormState<{ agentType?: AgentType }>).values
      ?.agentType !== AgentType.Voice
  );
}

export function BasicConfigFields() {
  const { hasAccess } = useAccess();
  const { t } = useLingui();
  const { initialValues } = useFormContext<ChatType>();
  const translations = useEnumTranslations();
  const { organization } = useCurrentOrganization();

  const canUseVoice = organization?.capabilities.voice;

  const { schema: agentTypeSchema } = useFieldsSchema(
    [
      {
        name: 'agentType',
        label: t`Agent type`,
        type: 'segmented',
        options: {
          [AgentType.Chat]: (
            <span className='flex items-center gap-1.5'>
              <MessageCircle className='size-4' />
              {translations.agentType.chat}
            </span>
          ),
          [AgentType.Voice]: (
            <span className='flex items-center gap-1.5'>
              <PhoneCall className='size-4' />
              {translations.agentType.voice}
            </span>
          ),
        },
        disabledOptions: canUseVoice
          ? undefined
          : {
              [AgentType.Voice]: t`Voice agents are not enabled for this organization`,
            },
        defaultValue: AgentType.Chat,
        required: true,
      } as FormSchemaFieldsDef,
    ],
    [t, hasAccess, initialValues, canUseVoice],
  );

  const { schema: identitySchema } = useFieldsSchema(
    [
      {
        name: 'name',
        label: t`Name`,
        defaultValue: AgentDefaults.name,
        required: true,
      },
    ],
    [t, hasAccess, initialValues],
  );

  const { schema: personalizationSchema } = useFieldsSchema(
    [
      {
        name: 'agentConfig.personalization',
        label: t`Agent instructions`,
        type: 'richtext',
        uiProps: {
          enableMarkdownPasteTransform: true,
        },
        required: true,
        defaultValue: t`Role, audience and purpose: You are a helpful assistant for our company. Help customers understand our products and services and choose a useful next step. Adapt this section to describe who you help and what a successful conversation should achieve.

Scope and terminology: Answer within our business scope. Specify the topics you cover, any topics to redirect, and company names or terms you should use. Refer unrelated requests back to our services. Describe any specialist capabilities you explicitly want to enable.

Knowledge and uncertainty: Base company-specific facts, policies, prices and availability on the available knowledge and tools. Specify preferred sources if relevant. When information is missing or conflicting, explain what is uncertain and ask for clarification or offer support.

Workflow: Answer directly when the request is clear. Ask a focused question when essential details are missing. Carry forward the customer's requirements and use their latest corrections. Add any required steps, eligibility conditions or questions for your business here.

Response preferences: Be friendly, clear and concise. Use the customer's language, explain unfamiliar terms and adapt detail to the question. Specify your preferred tone, formatting, length and language conventions here.

Escalation: Offer human support when information is unavailable or the request needs a person. Add the correct contact details and conditions for escalation. Do not promise a callback, booking or other action unless it has actually been arranged using an available tool.

Examples: Add representative customer questions and the responses you want. Include a normal request, an ambiguous request, a correction, missing information and an out-of-scope request. For product recommendations, respect the latest requirements and use verified product information.`,
        tooltip: t`Customize the agent's role, audience, allowed topics, terminology, sources, workflows, response style and escalation rules. Include examples and any explicitly enabled specialist capabilities. Detailed free-text instructions are supported.`,
      },
    ],
    [t, hasAccess, initialValues],
  );

  const { schema: advancedSchema } = useFieldsSchema(
    [
      {
        name: 'agentConfig.tonePreset',
        label: t`Tone`,
        type: 'select',
        options: translations.tonePreset,
        defaultValue: TonePreset.Casual,
        showOptional: false,
      },
      {
        // Inert on voice: `buildVoiceSystemPrompt` hard-codes spoken
        // brevity, so this preset has no effect on a voice chat.
        $relevant: {
          $when: isRelevantForChatOnly,
          $fields: [
            {
              name: 'agentConfig.responseLengthPreset',
              label: t`Response length`,
              type: 'select',
              options: translations.responseLengthPreset,
              defaultValue: ResponseLengthPreset.Short,
              showOptional: false,
              keepState: true,
            },
          ],
        },
      },
      {
        name: 'agentConfig.language',
        label: t`Language`,
        type: 'select',
        options: {
          automatic: t`Automatic`,
          ...translations.supportedLanguages,
        },
        defaultValue: 'cs',
        showOptional: false,
      },
      {
        $relevant: {
          $when: ({ formState }) =>
            (formState?.values as { agentConfig?: { language?: string } })
              ?.agentConfig?.language === 'automatic',
          $fields: [
            {
              name: 'agentConfig.fallbackLanguage',
              label: t`Fallback language`,
              type: 'select',
              options: translations.supportedLanguages,
              defaultValue: 'cs',
              showOptional: false,
              tooltip: t`Used when language is set to Automatic and the user's language cannot be detected.`,
            },
          ],
        },
      },
      {
        name: 'agentConfig.gender',
        label: t`Gender`,
        type: 'select',
        options: translations.gender,
        defaultValue: Gender.None,
        showOptional: false,
      },
      {
        // Inert on voice: `buildVoiceRunContext` force-overrides all three
        // to `false`, so the toggles would let operators change values the
        // runtime discards. `keepState` preserves them for a chat later
        // switched back from Voice to Chat.
        $relevant: {
          $when: isRelevantForChatOnly,
          $fields: [
            {
              name: 'agentConfig.showSources',
              label: t`Show sources`,
              type: 'checkbox',
              uiControl: 'switch',
              defaultValue: true,
              required: true,
              keepState: true,
            },
            {
              name: 'agentConfig.showFollowUpQuestions',
              label: t`Show follow-up questions`,
              type: 'checkbox',
              uiControl: 'switch',
              tooltip: t`Disabling this option will hide follow-up questions.`,
              defaultValue: true,
              required: true,
              keepState: true,
            },
            {
              name: 'agentConfig.imageSupport',
              label: t`Image support`,
              type: 'checkbox',
              uiControl: 'switch',
              defaultValue: true,
              showOptional: false,
              keepState: true,
            },
          ],
        },
      },
    ],
    [t, hasAccess, initialValues, canUseVoice],
  );

  return (
    <Card title={t`Basic details`}>
      <SchemaFields schema={agentTypeSchema} />

      <div className='mt-2'>
        <AgentSelector name='agent' required label={t`Agent`} />
      </div>
      <div className='mt-2'>
        <SchemaFields schema={identitySchema} />
      </div>
      <div className='mt-2'>
        <SchemaFields schema={personalizationSchema} />
      </div>
      <details className='mt-2 rounded-md border border-border p-3'>
        <summary className='cursor-pointer text-sm font-medium'>
          {t`Advanced settings`}
        </summary>
        <p className='mt-2 text-xs text-placeholder'>
          {t`These advanced settings are default behavior and some of them can be overridden by the Personalization above.`}
        </p>
        <div className='mt-3 flex flex-col gap-3'>
          <SchemaFields schema={advancedSchema} />
        </div>
      </details>
    </Card>
  );
}
